import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  MapPin, 
  CreditCard, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Shield,
  Calendar,
  Phone,
  Mail
} from 'lucide-react';

const CustomerEntry = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Customer Information
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    phone: '',
    email: '',
    
    // Address Information
    address: '',
    city: '',
    state: '',
    zipCode: '',
    idNumber: '',
    idState: '',
    
    // Firearm Information
    firearmType: '',
    serialNumber: '',
    manufacturer: '',
    model: '',
    caliber: '',
    purchasePrice: '',
    
    // Compliance Information
    fflNumber: '',
    backgroundCheckRequired: true,
    waitingPeriod: 0,
    stateCompliance: {}
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const states = [
    { code: 'AL', name: 'Alabama', waitingPeriod: 0, backgroundCheck: true },
    { code: 'AK', name: 'Alaska', waitingPeriod: 0, backgroundCheck: true },
    { code: 'AZ', name: 'Arizona', waitingPeriod: 0, backgroundCheck: true },
    { code: 'AR', name: 'Arkansas', waitingPeriod: 0, backgroundCheck: true },
    { code: 'CA', name: 'California', waitingPeriod: 10, backgroundCheck: true },
    { code: 'CO', name: 'Colorado', waitingPeriod: 0, backgroundCheck: true },
    { code: 'CT', name: 'Connecticut', waitingPeriod: 14, backgroundCheck: true },
    { code: 'DE', name: 'Delaware', waitingPeriod: 0, backgroundCheck: true },
    { code: 'FL', name: 'Florida', waitingPeriod: 3, backgroundCheck: true },
    { code: 'GA', name: 'Georgia', waitingPeriod: 0, backgroundCheck: true },
    { code: 'HI', name: 'Hawaii', waitingPeriod: 14, backgroundCheck: true },
    { code: 'ID', name: 'Idaho', waitingPeriod: 0, backgroundCheck: true },
    { code: 'IL', name: 'Illinois', waitingPeriod: 72, backgroundCheck: true },
    { code: 'IN', name: 'Indiana', waitingPeriod: 0, backgroundCheck: true },
    { code: 'IA', name: 'Iowa', waitingPeriod: 0, backgroundCheck: true },
    { code: 'KS', name: 'Kansas', waitingPeriod: 0, backgroundCheck: true },
    { code: 'KY', name: 'Kentucky', waitingPeriod: 0, backgroundCheck: true },
    { code: 'LA', name: 'Louisiana', waitingPeriod: 0, backgroundCheck: true },
    { code: 'ME', name: 'Maine', waitingPeriod: 0, backgroundCheck: true },
    { code: 'MD', name: 'Maryland', waitingPeriod: 7, backgroundCheck: true },
    { code: 'MA', name: 'Massachusetts', waitingPeriod: 0, backgroundCheck: true },
    { code: 'MI', name: 'Michigan', waitingPeriod: 0, backgroundCheck: true },
    { code: 'MN', name: 'Minnesota', waitingPeriod: 0, backgroundCheck: true },
    { code: 'MS', name: 'Mississippi', waitingPeriod: 0, backgroundCheck: true },
    { code: 'MO', name: 'Missouri', waitingPeriod: 0, backgroundCheck: true },
    { code: 'MT', name: 'Montana', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NE', name: 'Nebraska', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NV', name: 'Nevada', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NH', name: 'New Hampshire', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NJ', name: 'New Jersey', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NM', name: 'New Mexico', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NY', name: 'New York', waitingPeriod: 0, backgroundCheck: true },
    { code: 'NC', name: 'North Carolina', waitingPeriod: 0, backgroundCheck: true },
    { code: 'ND', name: 'North Dakota', waitingPeriod: 0, backgroundCheck: true },
    { code: 'OH', name: 'Ohio', waitingPeriod: 0, backgroundCheck: true },
    { code: 'OK', name: 'Oklahoma', waitingPeriod: 0, backgroundCheck: true },
    { code: 'OR', name: 'Oregon', waitingPeriod: 0, backgroundCheck: true },
    { code: 'PA', name: 'Pennsylvania', waitingPeriod: 0, backgroundCheck: true },
    { code: 'RI', name: 'Rhode Island', waitingPeriod: 0, backgroundCheck: true },
    { code: 'SC', name: 'South Carolina', waitingPeriod: 0, backgroundCheck: true },
    { code: 'SD', name: 'South Dakota', waitingPeriod: 0, backgroundCheck: true },
    { code: 'TN', name: 'Tennessee', waitingPeriod: 0, backgroundCheck: true },
    { code: 'TX', name: 'Texas', waitingPeriod: 0, backgroundCheck: true },
    { code: 'UT', name: 'Utah', waitingPeriod: 0, backgroundCheck: true },
    { code: 'VT', name: 'Vermont', waitingPeriod: 0, backgroundCheck: true },
    { code: 'VA', name: 'Virginia', waitingPeriod: 0, backgroundCheck: true },
    { code: 'WA', name: 'Washington', waitingPeriod: 10, backgroundCheck: true },
    { code: 'WV', name: 'West Virginia', waitingPeriod: 0, backgroundCheck: true },
    { code: 'WI', name: 'Wisconsin', waitingPeriod: 0, backgroundCheck: true },
    { code: 'WY', name: 'Wyoming', waitingPeriod: 0, backgroundCheck: true }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Update state-specific compliance when state changes
    if (field === 'state') {
      const selectedState = states.find(s => s.code === value);
      if (selectedState) {
        setFormData(prev => ({
          ...prev,
          waitingPeriod: selectedState.waitingPeriod,
          backgroundCheckRequired: selectedState.backgroundCheck,
          stateCompliance: {
            state: selectedState.name,
            waitingPeriod: selectedState.waitingPeriod,
            backgroundCheck: selectedState.backgroundCheck,
            additionalRequirements: getStateRequirements(selectedState.code)
          }
        }));
      }
    }
  };

  const getStateRequirements = (stateCode) => {
    const requirements = {
      'CA': ['Firearm Safety Certificate', 'Handgun Safety Certificate', '10-day waiting period'],
      'NY': ['Pistol Permit', 'Background check', 'Mental health records check'],
      'IL': ['FOID Card', '72-hour waiting period', 'Background check'],
      'CT': ['Pistol Permit', '14-day waiting period', 'Safety course'],
      'NJ': ['Firearms ID Card', 'Pistol Permit', 'Background check'],
      'MD': ['Handgun Qualification License', '7-day waiting period'],
      'MA': ['License to Carry', 'Background check', 'Safety course'],
      'HI': ['Permit to Acquire', '14-day waiting period'],
      'WA': ['Background check', '10-day waiting period'],
      'FL': ['3-day waiting period', 'Background check']
    };
    return requirements[stateCode] || ['Background check required'];
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.ssn) newErrors.ssn = 'SSN is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
    } else if (step === 2) {
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.state) newErrors.state = 'State is required';
      if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
      if (!formData.idNumber) newErrors.idNumber = 'ID number is required';
      if (!formData.idState) newErrors.idState = 'ID state is required';
    } else if (step === 3) {
      if (!formData.firearmType) newErrors.firearmType = 'Firearm type is required';
      if (!formData.serialNumber) newErrors.serialNumber = 'Serial number is required';
      if (!formData.manufacturer) newErrors.manufacturer = 'Manufacturer is required';
      if (!formData.model) newErrors.model = 'Model is required';
      if (!formData.caliber) newErrors.caliber = 'Caliber is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    try {
      // Submit to backend
      const apiUrl = process.env.REACT_APP_API_URL || (window.location.hostname?.includes('darkmattersolutions') ? 'https://app.darkmattersolutions.ai' : 'http://129.146.2.109');
      const response = await fetch(`${apiUrl}/api/form4473/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Form 4473 submitted successfully!');
        // Reset form
        setFormData({
          firstName: '', lastName: '', dateOfBirth: '', ssn: '', phone: '', email: '',
          address: '', city: '', state: '', zipCode: '', idNumber: '', idState: '',
          firearmType: '', serialNumber: '', manufacturer: '', model: '', caliber: '', purchasePrice: '',
          fflNumber: '', backgroundCheckRequired: true, waitingPeriod: 0, stateCompliance: {}
        });
        setCurrentStep(1);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <User className="h-12 w-12 text-blue-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white">Customer Information</h2>
        <p className="text-gray-400">Enter customer's personal details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.firstName ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Enter first name"
          />
          {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Last Name *</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.lastName ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Enter last name"
          />
          {errors.lastName && <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Date of Birth *</label>
          <input
            type="date"
            value={formData.dateOfBirth}
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.dateOfBirth ? 'border-red-500' : 'border-gray-600'
            }`}
          />
          {errors.dateOfBirth && <p className="text-red-400 text-sm mt-1">{errors.dateOfBirth}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">SSN (Last 4 digits) *</label>
          <input
            type="text"
            value={formData.ssn}
            onChange={(e) => handleInputChange('ssn', e.target.value)}
            maxLength={4}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.ssn ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="XXXX"
          />
          {errors.ssn && <p className="text-red-400 text-sm mt-1">{errors.ssn}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number *</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.phone ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="(555) 123-4567"
          />
          {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="customer@email.com"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <MapPin className="h-12 w-12 text-green-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white">Address Information</h2>
        <p className="text-gray-400">Enter customer's current address</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-300 mb-2">Street Address *</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.address ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="123 Main Street"
          />
          {errors.address && <p className="text-red-400 text-sm mt-1">{errors.address}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">City *</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.city ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Anytown"
          />
          {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">State *</label>
          <select
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.state ? 'border-red-500' : 'border-gray-600'
            }`}
          >
            <option value="">Select State</option>
            {states.map(state => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
          {errors.state && <p className="text-red-400 text-sm mt-1">{errors.state}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">ZIP Code *</label>
          <input
            type="text"
            value={formData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.zipCode ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="12345"
          />
          {errors.zipCode && <p className="text-red-400 text-sm mt-1">{errors.zipCode}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">ID Number *</label>
          <input
            type="text"
            value={formData.idNumber}
            onChange={(e) => handleInputChange('idNumber', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.idNumber ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Driver's License Number"
          />
          {errors.idNumber && <p className="text-red-400 text-sm mt-1">{errors.idNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">ID State *</label>
          <input
            type="text"
            value={formData.idState}
            onChange={(e) => handleInputChange('idState', e.target.value)}
            maxLength={2}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.idState ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="IL"
          />
          {errors.idState && <p className="text-red-400 text-sm mt-1">{errors.idState}</p>}
        </div>
      </div>

      {/* State Compliance Requirements */}
      {formData.stateCompliance.state && (
        <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-400 mb-2 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            {formData.stateCompliance.state} Compliance Requirements
          </h3>
          <div className="space-y-2">
            <div className="flex items-center text-sm text-gray-300">
              <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
              Background Check: {formData.stateCompliance.backgroundCheck ? 'Required' : 'Not Required'}
            </div>
            {formData.stateCompliance.waitingPeriod > 0 && (
              <div className="flex items-center text-sm text-gray-300">
                <Calendar className="h-4 w-4 text-yellow-400 mr-2" />
                Waiting Period: {formData.stateCompliance.waitingPeriod} days
              </div>
            )}
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-300 mb-1">Additional Requirements:</p>
              <ul className="list-disc list-inside space-y-1">
                {formData.stateCompliance.additionalRequirements?.map((req, index) => (
                  <li key={index} className="text-sm text-gray-400">{req}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <FileText className="h-12 w-12 text-purple-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white">Firearm Information</h2>
        <p className="text-gray-400">Enter firearm details for transfer</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Firearm Type *</label>
          <select
            value={formData.firearmType}
            onChange={(e) => handleInputChange('firearmType', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.firearmType ? 'border-red-500' : 'border-gray-600'
            }`}
          >
            <option value="">Select Type</option>
            <option value="handgun">Handgun</option>
            <option value="rifle">Rifle</option>
            <option value="shotgun">Shotgun</option>
            <option value="other">Other</option>
          </select>
          {errors.firearmType && <p className="text-red-400 text-sm mt-1">{errors.firearmType}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Serial Number *</label>
          <input
            type="text"
            value={formData.serialNumber}
            onChange={(e) => handleInputChange('serialNumber', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.serialNumber ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="ABC123456"
          />
          {errors.serialNumber && <p className="text-red-400 text-sm mt-1">{errors.serialNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Manufacturer *</label>
          <input
            type="text"
            value={formData.manufacturer}
            onChange={(e) => handleInputChange('manufacturer', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.manufacturer ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="Glock, Smith & Wesson, etc."
          />
          {errors.manufacturer && <p className="text-red-400 text-sm mt-1">{errors.manufacturer}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Model *</label>
          <input
            type="text"
            value={formData.model}
            onChange={(e) => handleInputChange('model', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.model ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="G19, M&P, etc."
          />
          {errors.model && <p className="text-red-400 text-sm mt-1">{errors.model}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Caliber *</label>
          <input
            type="text"
            value={formData.caliber}
            onChange={(e) => handleInputChange('caliber', e.target.value)}
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.caliber ? 'border-red-500' : 'border-gray-600'
            }`}
            placeholder="9mm, .45 ACP, etc."
          />
          {errors.caliber && <p className="text-red-400 text-sm mt-1">{errors.caliber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Purchase Price</label>
          <input
            type="number"
            value={formData.purchasePrice}
            onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="500"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-400'
              }`}>
                1
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-400'
              }`}>
                2
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-400'
              }`}>
                3
              </div>
            </div>
            <div className="text-sm text-gray-400">
              Step {currentStep} of 3
            </div>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-gray-800 rounded-lg p-8"
        >
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                currentStep === 1
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Previous
            </button>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                Next
                <ArrowRight className="h-5 w-5 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Form 4473'}
                <CheckCircle className="h-5 w-5 ml-2" />
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerEntry;
